'use client';

import { CSSProperties, FC, FormEvent, useState } from 'react';

import GoogleMapEmbed from '@/components/common/google-map-embed/google-map-embed';
import { Button, Input, Textarea } from '@/components/ui';
import { useTranslation } from '@/providers/app-provider/app-provider';

type ContactSectionProps = {
  title: string;
  description: string;
  email: string;
  mapEmbedUrl: string;
  submitLabel?: string;
  titleColor?: string;
  descriptionColor?: string;
  labelColor?: string;
  paddingStyle?: CSSProperties;
};

const ContactSection: FC<ContactSectionProps> = ({
  title,
  description,
  email,
  mapEmbedUrl,
  submitLabel,
  titleColor,
  descriptionColor,
  labelColor,
  paddingStyle,
}) => {
  const { t } = useTranslation();
  const [name, setName] = useState('');
  const [senderEmail, setSenderEmail] = useState('');
  const [message, setMessage] = useState('');

  const mailtoHref = `mailto:${encodeURIComponent(email)}?subject=${encodeURIComponent(`Contact from ${name} (${senderEmail})`)}&body=${encodeURIComponent(message)}`;
  const fieldLabelClassNames = labelColor
    ? { label: '!text-[var(--contact-label-color)]' }
    : undefined;
  const formStyle = labelColor
    ? ({ ['--contact-label-color' as string]: labelColor } as CSSProperties)
    : undefined;

  const handleSubmit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    window.location.href = mailtoHref;
  };

  return (
    <div className="container mx-auto" style={paddingStyle}>
      <div className="grid grid-cols-1 items-center gap-12 @md:grid-cols-2">
        <div>
          <h1
            className="mb-4 text-4xl font-bold"
            style={titleColor ? { color: titleColor } : undefined}
          >
            {title}
          </h1>
          <p
            className="mb-8 text-gray-700"
            style={descriptionColor ? { color: descriptionColor } : undefined}
          >
            {description}
          </p>

          <form
            className="mt-4 flex flex-col gap-4"
            style={formStyle}
            onSubmit={handleSubmit}
            noValidate
          >
            <Input
              type="text"
              name="name"
              label={t?.common.contactForm.name}
              placeholder={t?.common.contactForm.namePlaceholder}
              labelPlacement="outside"
              classNames={fieldLabelClassNames}
              value={name}
              onValueChange={setName}
              isRequired
              autoComplete="name"
            />

            <Input
              type="email"
              name="email"
              label={t?.common.contactForm.email}
              placeholder={t?.common.contactForm.emailPlaceholder}
              labelPlacement="outside"
              classNames={fieldLabelClassNames}
              value={senderEmail}
              onValueChange={setSenderEmail}
              isRequired
              autoComplete="email"
            />

            <Textarea
              name="message"
              label={t?.common.contactForm.message}
              placeholder={t?.common.contactForm.messagePlaceholder}
              labelPlacement="outside"
              classNames={fieldLabelClassNames}
              value={message}
              onValueChange={setMessage}
              isRequired
              minRows={5}
            />

            <Button type="submit" color="primary" className="w-full">
              {submitLabel || t?.common.contactForm.submit}
            </Button>
          </form>
        </div>

        <div className="flex h-full w-full justify-center">
          <GoogleMapEmbed mapEmbedUrl={mapEmbedUrl} className="w-full" />
        </div>
      </div>
    </div>
  );
};

export default ContactSection;
